import { motion, type MotionValue, useTransform } from "motion/react";
import { servicesSlides } from "@/config/services.config";
import {
  categoryStartIndexes,
  serviceTimeline,
} from "@/config/service-timeline";
import { cn } from "@/lib/utils";

function StepBar({
  index,
  progress,
}: {
  index: number;
  progress: MotionValue<number>;
}) {
  const total = serviceTimeline.length;
  const fill = useTransform(
    progress,
    [index / total, (index + 1) / total],
    [0, 1],
    { clamp: true }
  );
  // Reveal with a clip rather than scaleX so the gradient isn't squashed.
  const clipPath = useTransform(fill, (v) => `inset(0 ${(1 - v) * 100}% 0 0)`);

  return (
    <span className="relative h-full min-w-0 flex-1 overflow-hidden rounded-full bg-white/15">
      <motion.span
        className="absolute inset-0 bg-no-repeat"
        style={{
          clipPath,
          // Each step shows its own slice of one gradient spanning the whole
          // bar, so the color runs continuously from the first step to the last.
          backgroundImage: "linear-gradient(to right, #149CFF, #DEFF77)",
          backgroundSize: `${total * 100}% 100%`,
          backgroundPosition: `${(index / (total - 1)) * 100}% 0`,
        }}
      />
    </span>
  );
}

interface ServiceProgressProps {
  activeCategoryIndex: number;
  scrollProgress: MotionValue<number>;
  onSelectCategory: (index: number) => void;
}

/**
 * Every service step as a bar, grouped by category. Each group doubles as a
 * jump link to that category.
 */
export default function ServiceProgress({
  activeCategoryIndex,
  scrollProgress,
  onSelectCategory,
}: ServiceProgressProps) {
  return (
    <nav
      aria-label="Service categories"
      className="flex min-w-0 flex-1 items-center gap-2.5"
    >
      {servicesSlides.map((category, categoryIndex) => {
        const isActive = categoryIndex === activeCategoryIndex;
        const start = categoryStartIndexes[categoryIndex]!;

        return (
          <button
            key={category.title}
            type="button"
            onClick={() => onSelectCategory(categoryIndex)}
            aria-label={`Jump to ${category.title}`}
            aria-current={isActive ? "step" : undefined}
            style={{ flexGrow: category.slides.length }}
            className="group relative flex h-8 min-w-0 basis-0 items-center rounded-md"
          >
            <span
              className={cn(
                "flex h-1 w-full gap-[3px] transition-opacity duration-500",
                isActive ? "opacity-100" : "opacity-50 group-hover:opacity-90"
              )}
            >
              {category.slides.map((slide, slideIndex) => (
                <StepBar
                  key={slide.title}
                  index={start + slideIndex}
                  progress={scrollProgress}
                />
              ))}
            </span>
            <span className="pointer-events-none absolute bottom-full left-1/2 mb-1 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-md bg-[#0B1824]/90 px-2 py-1 text-[10px] text-white/80 opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100">
              {category.shortTitle}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
